import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ChevronLeft, ChevronRight, Play, X } from "lucide-react";
import { collectPostMedia } from "./postHelpers";

function layoutClass(count) {
  if (count <= 1) return "is-one";
  if (count === 2) return "is-two";
  return "is-three";
}

function StreamStill({ src }) {
  return (
    <video
      className="posts-feed-still"
      src={src}
      muted
      playsInline
      preload="metadata"
      tabIndex={-1}
      onLoadedMetadata={(event) => {
        const video = event.currentTarget;
        if (video.currentTime > 0) return;
        const duration = Number(video.duration);
        const mark = Number.isFinite(duration) && duration > 0.4 ? 0.2 : 0;
        if (mark) video.currentTime = mark;
      }}
    />
  );
}

function MediaLightbox({ media, index, onClose }) {
  const [page, setPage] = useState(index);
  const item = media[page];
  const hasPrev = page > 0;
  const hasNext = page < media.length - 1;

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowRight") setPage((current) => Math.min(media.length - 1, current + 1));
      if (event.key === "ArrowLeft") setPage((current) => Math.max(0, current - 1));
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [media.length, onClose]);

  if (!item) return null;

  return createPortal(
    <div className="post-lightbox" role="dialog" aria-modal="true" aria-label="Post media">
      <button type="button" className="post-lightbox-backdrop" aria-label="Close" onClick={onClose} />
      <button type="button" className="post-lightbox-close" aria-label="Close" onClick={onClose}>
        <X size={20} />
      </button>
      {hasPrev ? (
        <button
          type="button"
          className="post-lightbox-nav is-prev"
          aria-label="Previous"
          onClick={() => setPage((current) => current - 1)}
        >
          <ChevronLeft size={22} />
        </button>
      ) : null}
      {hasNext ? (
        <button
          type="button"
          className="post-lightbox-nav is-next"
          aria-label="Next"
          onClick={() => setPage((current) => current + 1)}
        >
          <ChevronRight size={22} />
        </button>
      ) : null}
      <div className="post-lightbox-stage">
        {item.kind === "video" ? (
          <video
            key={item.src}
            src={item.src}
            poster={item.poster || undefined}
            controls
            autoPlay
            playsInline
            preload="metadata"
          />
        ) : (
          <img src={item.src} alt="" />
        )}
      </div>
      <p className="post-lightbox-count">
        {page + 1} / {media.length}
      </p>
    </div>,
    document.body
  );
}

export default function PostMediaGrid({ post, className = "" }) {
  const media = collectPostMedia(post);
  const [openIndex, setOpenIndex] = useState(null);

  if (!media.length) return null;

  const preview = media.length > 3 ? media.slice(0, 3) : media;
  const extra = Math.max(0, media.length - 3);
  const layout = layoutClass(media.length);

  return (
    <>
      <div className={`posts-feed-media is-grid ${layout}${className ? ` ${className}` : ""}`}>
        {preview.map((item, index) => {
          const overlay = extra && index === preview.length - 1 ? extra : 0;
          return (
            <button
              key={item.id}
              type="button"
              className="posts-feed-tile"
              onClick={() => setOpenIndex(index)}
              aria-label={item.kind === "video" ? "Play video" : `Open photo ${index + 1}`}
            >
              {item.kind === "video" ? (
                item.poster ? (
                  <img src={item.poster} alt="" />
                ) : (
                  <StreamStill src={item.src} />
                )
              ) : (
                <img src={item.src} alt="" />
              )}
              {item.kind === "video" && !overlay ? (
                <span className="posts-feed-play" aria-hidden>
                  <span>
                    <Play size={22} fill="currentColor" />
                  </span>
                </span>
              ) : null}
              {overlay ? <span className="posts-feed-media-more">+{overlay}</span> : null}
            </button>
          );
        })}
      </div>
      {openIndex !== null ? (
        <MediaLightbox media={media} index={openIndex} onClose={() => setOpenIndex(null)} />
      ) : null}
    </>
  );
}
